import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { featureRows } from '../../data/features';
import { industries } from '../../data/industries';
import tecnvironsLogoGif from '../../assets/images/gif.mp4';
import './Navbar.css';

const links = [
  { to: '/', label: 'Home', end: true },
  { to: '/platform', label: 'Platform' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const loginOptions = [
  { label: 'Business Unit', userType: 'user' },
  { label: 'Head Quarters', userType: 'admin' },
  { label: 'Tecnvi (Internal)', href: 'https://admin-panel-d7dcawcthxcwb4hz.centralindia-01.azurewebsites.net/' },
];

const signupOptions = [
  { label: 'Head Quarters', userType: 'admin' },
];

export default function Navbar({ openLoginModal, openSignupModal }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [loginMenuOpen, setLoginMenuOpen] = useState(false);
  const [signupMenuOpen, setSignupMenuOpen] = useState(false);
  const solutionsTimer = useRef(null);
  const industriesTimer = useRef(null);
  const loginTimer = useRef(null);
  const signupTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSolutionsOpen(false);
    setIndustriesOpen(false);
    setLoginMenuOpen(false);
    setSignupMenuOpen(false);
  }, [location.pathname]);

  const isMobile = () => window.innerWidth <= 880;

  const openSolutions = () => {
    if (isMobile()) return;
    clearTimeout(solutionsTimer.current);
    setSolutionsOpen(true);
  };

  const closeSolutions = () => {
    if (isMobile()) return;
    solutionsTimer.current = setTimeout(() => setSolutionsOpen(false), 150);
  };

  const openIndustries = () => {
    if (isMobile()) return;
    clearTimeout(industriesTimer.current);
    setIndustriesOpen(true);
  };

  const closeIndustries = () => {
    if (isMobile()) return;
    industriesTimer.current = setTimeout(() => setIndustriesOpen(false), 150);
  };

  const openLoginMenu = () => {
    if (isMobile()) return;
    clearTimeout(loginTimer.current);
    setLoginMenuOpen(true);
  };

  const closeLoginMenu = () => {
    if (isMobile()) return;
    loginTimer.current = setTimeout(() => setLoginMenuOpen(false), 150);
  };

  const openSignupMenu = () => {
    if (isMobile()) return;
    clearTimeout(signupTimer.current);
    setSignupMenuOpen(true);
  };

  const closeSignupMenu = () => {
    if (isMobile()) return;
    signupTimer.current = setTimeout(() => setSignupMenuOpen(false), 150);
  };

  const handleLoginOption = (option) => {
    setLoginMenuOpen(false);
    setOpen(false);
    if (option.href) {
      window.open(option.href, '_blank', 'noopener,noreferrer');
      return;
    }
    openLoginModal?.(option.userType);
  };

  const handleSignupOption = (option) => {
    setSignupMenuOpen(false);
    setOpen(false);
    if (option.href) {
      window.open(option.href, '_blank', 'noopener,noreferrer');
      return;
    }
    openSignupModal?.(option.userType);
  };

  const goToIndustry = (id) => {
    setIndustriesOpen(false);
    setOpen(false);
    navigate('/industries');
    requestAnimationFrame(() => {
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    });
  };

  return (
    <header className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="container navbar-inner">
        <NavLink to="/" className="navbar-brand">
          <video
            src={tecnvironsLogoGif}
            className="navbar-logo"
            autoPlay
            loop
            muted
            playsInline
          />
          <span className="navbar-brand-text">Tecnvirons</span>
        </NavLink>

        <nav className={`navbar-links ${open ? 'open' : ''}`}>
          <NavLink
            to="/"
            end
            className={({ isActive }) => `navbar-link ${isActive ? 'active' : ''}`}
            onClick={() => setOpen(false)}
          >
            Home
          </NavLink>

          <div
            className="navbar-dropdown-wrap"
            onMouseEnter={openSolutions}
            onMouseLeave={closeSolutions}
          >
            <button
              type="button"
              className={`navbar-link navbar-dropdown-trigger ${solutionsOpen ? 'active' : ''}`}
              onClick={() => setSolutionsOpen((v) => !v)}
            >
              <span>Solutions</span>
              <span className="navbar-caret" />
            </button>

            <div className={`navbar-dropdown navbar-dropdown-solutions ${solutionsOpen ? 'open' : ''}`}>
              {featureRows.map((row) => (
                <div className="navbar-dropdown-row" key={row[0].slug}>
                  {row.map((feature) => (
                    <NavLink
                      key={feature.slug}
                      to={`/solutions/${feature.slug}`}
                      className="navbar-dropdown-item"
                      onClick={() => { setSolutionsOpen(false); setOpen(false); }}
                    >
                      <feature.icon size={18} strokeWidth={1.8} />
                      <span>{feature.name}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div
            className="navbar-dropdown-wrap"
            onMouseEnter={openIndustries}
            onMouseLeave={closeIndustries}
          >
            <NavLink
              to="/industries"
              className={({ isActive }) => `navbar-link navbar-dropdown-trigger ${isActive ? 'active' : ''} ${industriesOpen ? 'active' : ''}`}
              onClick={(e) => {
                if (isMobile()) {
                  e.preventDefault();
                  setIndustriesOpen((v) => !v);
                } else {
                  setOpen(false);
                }
              }}
            >
              <span>Industries</span>
              <span className="navbar-caret" />
            </NavLink>

            <div className={`navbar-dropdown navbar-dropdown-industries ${industriesOpen ? 'open' : ''}`}>
              <div className="navbar-dropdown-row">
                {industries.slice(0, 3).map((ind) => (
                  <button
                    key={ind.id}
                    type="button"
                    className="navbar-dropdown-item"
                    onClick={() => goToIndustry(ind.id)}
                  >
                    <ind.icon size={18} strokeWidth={1.8} />
                    <span>{ind.name}</span>
                  </button>
                ))}
              </div>
              <div className="navbar-dropdown-row">
                {industries.slice(3, 5).map((ind) => (
                  <button
                    key={ind.id}
                    type="button"
                    className="navbar-dropdown-item"
                    onClick={() => goToIndustry(ind.id)}
                  >
                    <ind.icon size={18} strokeWidth={1.8} />
                    <span>{ind.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {links.slice(1).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) => `navbar-link ${isActive ? 'active' : ''}`}
              onClick={() => setOpen(false)}
            >
              {link.label}
            </NavLink>
          ))}

          <div
            className="navbar-dropdown-wrap"
            onMouseEnter={openLoginMenu}
            onMouseLeave={closeLoginMenu}
          >
            <button
              type="button"
              className={`navbar-link navbar-dropdown-trigger ${loginMenuOpen ? 'active' : ''}`}
              onClick={() => setLoginMenuOpen((v) => !v)}
            >
              <span>Login</span>
              <span className="navbar-caret" />
            </button>

            <div className={`navbar-dropdown navbar-dropdown-auth ${loginMenuOpen ? 'open' : ''}`}>
              <div className="navbar-dropdown-row navbar-dropdown-row-single">
                {loginOptions.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className="navbar-dropdown-item"
                    onClick={() => handleLoginOption(option)}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div
            className="navbar-dropdown-wrap"
            onMouseEnter={openSignupMenu}
            onMouseLeave={closeSignupMenu}
          >
            <button
              type="button"
              className={`btn btn-primary navbar-cta navbar-dropdown-trigger ${signupMenuOpen ? 'active' : ''}`}
              onClick={() => setSignupMenuOpen((v) => !v)}
            >
              <span>Sign Up</span>
              <span className="navbar-caret" />
            </button>

            <div className={`navbar-dropdown navbar-dropdown-auth ${signupMenuOpen ? 'open' : ''}`}>
              <div className="navbar-dropdown-row navbar-dropdown-row-single">
                {signupOptions.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    className="navbar-dropdown-item"
                    onClick={() => handleSignupOption(option)}
                  >
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <button
          className={`navbar-toggle ${open ? 'open' : ''}`}
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  );
}
